/**
 * FIDDUX PDF Report Parser
 * Auto-detects and parses Markdown or JSON input into unified internal schema.
 */

import { marked } from 'marked';

// FIDDUX internal report schema (unified representation)
const DEFAULT_REPORT_META = {
  title: 'FIDDUX Report',
  title_pt: 'Relatório FIDDUX',
  date: new Date().toISOString().split('T')[0],
  language: ['pt', 'en'],
  document_type: 'analysis',
  logo_position: 'top-left',
};

/**
 * Detect input type: 'markdown' | 'json'
 */
export function detectInputType(content) {
  if (!content || typeof content !== 'string') return 'markdown';
  const trimmed = content.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) return 'json';
  return 'markdown';
}

/**
 * Parse markdown into internal JSON schema
 */
export function parseMarkdownToSchema(content) {
  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentTable = null;

  const flushSection = () => {
    if (currentSection) {
      const text = currentContent.join('\n').trim();
      if (text) {
        currentSection.content = text;
        currentSection.content_pt = text; // Same for markdown, can be edited later
      }
      if (currentTable) {
        currentSection.tables = [currentTable];
      }
      sections.push(currentSection);
    }
    currentSection = null;
    currentContent = [];
    currentTable = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const h1Match = line.match(/^#\s+(.+)$/);
    const h2Match = line.match(/^##\s+(.+)$/);
    const h3Match = line.match(/^###\s+(.+)$/);
    const tableSep = /^\|?[-:\s|]+\|?$/.test(line);
    const tableRow = line.includes('|') && line.trim().startsWith('|');

    if (h1Match) {
      flushSection();
      currentSection = {
        id: `s${sections.length + 1}`,
        order: sections.length + 1,
        type: 'text',
        title: h1Match[1].trim(),
        title_pt: h1Match[1].trim(),
        level: 1,
        content: '',
        content_pt: '',
        tables: [],
        charts: [],
        subsections: [],
      };
    } else if (h2Match && currentSection) {
      flushSection();
      currentSection = {
        id: `s${sections.length + 1}`,
        order: sections.length + 1,
        type: 'text',
        title: h2Match[1].trim(),
        title_pt: h2Match[1].trim(),
        level: 2,
        content: '',
        content_pt: '',
        tables: [],
        charts: [],
        subsections: [],
      };
    } else if (h3Match && currentSection) {
      currentContent.push(`**${h3Match[1].trim()}**`);
    } else if (tableRow && currentSection) {
      const cells = line.split('|').map((c) => c.trim()).filter(Boolean);
      if (!currentTable) {
        currentTable = { headers: cells, rows: [] };
      } else if (!tableSep) {
        currentTable.rows.push(cells);
      }
    } else if (currentSection && !tableSep) {
      if (line.trim() === '---') {
        flushSection();
      } else if (line.trim()) {
        currentContent.push(line);
      }
    }
  }
  flushSection();

  return {
    report_meta: { ...DEFAULT_REPORT_META },
    sections,
    footer: {
      disclaimer_pt: 'Este relatório é confidencial e destinado apenas a destinatários autorizados.',
      disclaimer_en: 'This report is confidential and intended for authorized recipients only.',
      branding_elements: {
        company_name: 'FIDDUX',
        accent_color: '#00d4ff',
        primary_color: '#1a1a2e',
        font_primary: 'Inter, sans-serif',
      },
    },
  };
}

/**
 * Normalize JSON string before parse (fix common copy-paste issues)
 */
function normalizeJsonString(str) {
  if (!str || typeof str !== 'string') return str;
  let s = str.trim();
  if (s.charCodeAt(0) === 0xfeff) s = s.slice(1);
  s = s.replace(/\u201c/g, '"').replace(/\u201d/g, '"').replace(/\u2018/g, "'").replace(/\u2019/g, "'");
  return s;
}

/**
 * Detect alternative JSON format (report_metadata or flat structure like WLCC)
 * Ou formato comparison (report + providers).
 */
function isAlternativeJsonFormat(parsed) {
  const noSections = !parsed.sections || !Array.isArray(parsed.sections) || parsed.sections.length === 0;
  if (!noSections) return false;
  const hasWLCC = parsed?.executive_summary || parsed?.business_model || parsed?.financial_projections
    || parsed?.executiveSummary || parsed?.businessModel || parsed?.financialProjections;
  const hasComparison = parsed?.report && Array.isArray(parsed?.providers);
  return hasWLCC || hasComparison;
}

/**
 * Convert object to markdown-like content for display
 */
function objToContent(obj, indent = 0) {
  if (obj == null) return '';
  if (typeof obj === 'string') return obj;
  if (typeof obj === 'number') return String(obj);
  if (Array.isArray(obj)) {
    return obj.map((item) => (typeof item === 'object' && item !== null ? objToContent(item, indent + 1) : `- ${item}`)).join('\n');
  }
  const lines = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v == null || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)) continue;
    const key = k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    if (typeof v === 'object' && v !== null && !Array.isArray(v) && typeof v.amount === 'undefined' && typeof v.currency === 'undefined') {
      lines.push(`**${key}**`);
      lines.push(objToContent(v, indent + 1));
    } else if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') {
      lines.push(`**${key}**`);
      v.forEach((item, i) => {
        lines.push(objToContent(item, indent + 1));
      });
    } else {
      lines.push(`**${key}:** ${Array.isArray(v) ? v.join(', ') : v}`);
    }
  }
  return lines.join('\n\n');
}

/**
 * Convert comparison format (report + providers) to standard schema
 */
function convertComparisonFormatToSchema(parsed) {
  const report_meta = {
    ...DEFAULT_REPORT_META,
    title: parsed.report || 'Report',
    title_pt: parsed.report || 'Relatório',
    date: parsed.date || new Date().toISOString().split('T')[0],
    cover_subtitle: parsed.filters_applied ? `Filters: ${parsed.filters_applied}` : '',
    cover_subtitle_pt: parsed.filters_applied ? `Filtros: ${parsed.filters_applied}` : '',
  };
  const sections = [];
  let order = 1;

  if (parsed.providers && parsed.providers.length > 0) {
    const headers = ['Rank', 'Name', 'HQ', 'Trust', 'Setup', 'Monthly', 'Tx %', 'Launch'];
    const headers_pt = ['Pos', 'Nome', 'Sede', 'Confiança', 'Setup', 'Mensal', 'Tx %', 'Lançamento'];
    const rows = parsed.providers.slice(0, 15).map((p) => [
      String(p.rank ?? ''),
      p.name || '',
      p.hq || '',
      p.trustscore || '',
      p.setup_cost?.amount != null ? `${p.setup_cost.amount} ${p.setup_cost.currency || 'EUR'}` : (p.setup_cost?.note || ''),
      typeof p.monthly_fee === 'number' ? String(p.monthly_fee) : (p.monthly_fee || ''),
      p.per_transaction || '',
      p.launch_time_weeks ? `${p.launch_time_weeks}w` : '',
    ]);
    sections.push({
      id: 'providers',
      order: order++,
      type: 'section',
      title: `Providers (${parsed.total_providers_analyzed || parsed.providers.length} analyzed)`,
      title_pt: `Provedores (${parsed.total_providers_analyzed || parsed.providers.length} analisados)`,
      level: 1,
      include_page_break: true,
      content: '',
      tables: [{ headers, headers_pt, rows }],
      subsections: [],
    });
  }

  if (parsed.cost_comparison_table) {
    const cct = parsed.cost_comparison_table;
    const content = [
      cct.cheapest_setup && `**Cheapest setup:** ${cct.cheapest_setup.provider} — ${cct.cheapest_setup.cost} (${cct.cheapest_setup.condition || ''})`,
      cct.lowest_per_transaction && `**Lowest per transaction:** ${cct.lowest_per_transaction.provider} — ${cct.lowest_per_transaction.cost}`,
      cct.best_value_for_fiddux_profile && `**Best value for FIDDUX:** ${cct.best_value_for_fiddux_profile.top_choice} — ${cct.best_value_for_fiddux_profile.reason}`,
    ].filter(Boolean).join('\n\n');
    sections.push({
      id: 'cost_comparison',
      order: order++,
      type: 'section',
      title: 'Cost Comparison',
      title_pt: 'Comparativo de Custos',
      level: 1,
      include_page_break: true,
      content: content || objToContent(cct),
      subsections: [],
      tables: [],
    });
  }

  if (parsed.ranking_for_fiddux && parsed.ranking_for_fiddux.length > 0) {
    const headers = ['Rank', 'Name', 'Score', 'Reason', 'Recommendation'];
    const headers_pt = ['Pos', 'Nome', 'Score', 'Motivo', 'Recomendação'];
    const rows = parsed.ranking_for_fiddux.map((r) => [
      String(r.rank ?? ''),
      r.name || '',
      String(r.score ?? ''),
      (r.reason || '').slice(0, 80) + (r.reason?.length > 80 ? '...' : ''),
      r.recommendation || '',
    ]);
    sections.push({
      id: 'ranking',
      order: order++,
      type: 'section',
      title: 'Ranking for FIDDUX',
      title_pt: 'Ranking para FIDDUX',
      level: 1,
      include_page_break: true,
      content: '',
      tables: [{ headers, headers_pt, rows }],
      subsections: [],
    });
  }

  if (parsed.final_recommendation) {
    const fr = parsed.final_recommendation;
    const content = [
      fr.chosen && `**Chosen:** ${fr.chosen}`,
      fr.reasoning?.length && `**Reasoning:**\n${fr.reasoning.map((r) => `- ${r}`).join('\n')}`,
      fr.action_items?.length && `**Action items:**\n${fr.action_items.map((a) => `- ${a}`).join('\n')}`,
    ].filter(Boolean).join('\n\n');
    sections.push({
      id: 'final_recommendation',
      order: order++,
      type: 'section',
      title: 'Final Recommendation',
      title_pt: 'Recomendação Final',
      level: 1,
      include_page_break: true,
      content: content || objToContent(fr),
      subsections: [],
      tables: [],
    });
  }

  if (parsed.fiddux_scenario_costs) {
    const fsc = parsed.fiddux_scenario_costs;
    const proj = fsc.provider_projections_month_1;
    if (proj && proj.length > 0) {
      const headers = ['Provider', 'Setup', 'Monthly', 'Txn', 'Cards', 'Total', 'Revenue', 'Margin'];
      const headers_pt = ['Provedor', 'Setup', 'Mensal', 'Txn', 'Cards', 'Total', 'Receita', 'Margem'];
      const rows = proj.map((p) => [
        p.provider || '',
        String(p.setup ?? ''),
        String(p.monthly ?? ''),
        String(p.txn_cost ?? ''),
        String(p.card_cost ?? ''),
        String(p.total_cost_eur ?? ''),
        String(p.fiddux_revenue_2p5pct ?? ''),
        String(p.margin_eur ?? ''),
      ]);
      sections.push({
        id: 'fiddux_scenario',
        order: order++,
        type: 'section',
        title: 'FIDDUX Scenario (Month 1)',
        title_pt: 'Cenário FIDDUX (Mês 1)',
        level: 1,
        include_page_break: true,
        content: fsc.assumptions ? `**Assumptions:** ${fsc.assumptions}` : '',
        tables: [{ headers, headers_pt, rows }],
        subsections: [],
      });
    }
  }

  if (parsed.why_linkedin_missing) {
    sections.push({
      id: 'methodology',
      order: order++,
      type: 'section',
      title: 'Methodology Note',
      title_pt: 'Nota Metodológica',
      level: 1,
      include_page_break: true,
      content: parsed.why_linkedin_missing,
      subsections: [],
      tables: [],
    });
  }

  return { report_meta, sections };
}

/**
 * Convert WLCC-style (report_metadata + flat) JSON to standard schema
 */
function convertAlternativeFormatToSchema(parsed) {
  if (parsed.report && Array.isArray(parsed.providers)) {
    return convertComparisonFormatToSchema(parsed);
  }
  const altMeta = parsed.report_metadata || parsed.reportMetadata || {};
  const stdMeta = parsed.report_meta || {};
  const report_meta = {
    ...DEFAULT_REPORT_META,
    ...stdMeta,
    title: altMeta.title || stdMeta.title || 'Financial Report',
    title_pt: altMeta.title_pt || stdMeta.title_pt || altMeta.title || stdMeta.title || 'Relatório Financeiro',
    date: altMeta.date_generated || altMeta.date || stdMeta.date || new Date().toISOString().split('T')[0],
    prepared_by: altMeta.prepared_for ? `Prepared for ${altMeta.prepared_for}` : altMeta.provider || stdMeta.prepared_by || 'FIDDUX',
    prepared_by_pt: altMeta.prepared_for ? `Preparado para ${altMeta.prepared_for}` : altMeta.provider || stdMeta.prepared_by_pt || 'FIDDUX',
    cover_subtitle: altMeta.provider || stdMeta.cover_subtitle || '',
    cover_subtitle_pt: altMeta.provider || stdMeta.cover_subtitle_pt || '',
  };

  const sections = [];
  let order = 1;
  const es = parsed.executive_summary || parsed.executiveSummary;
  const bm = parsed.business_model || parsed.businessModel;
  const fp = parsed.financial_projections || parsed.financialProjections;
  const as = parsed.annual_summary || parsed.annualSummary;
  const ftc = parsed.fiat_to_crypto_flow || parsed.fiatToCryptoFlow;
  const rm = parsed.risk_mitigation || parsed.riskMitigation;
  const rec = parsed.recommendations;
  const app = parsed.appendix;

  if (es) {
    const content = [
      es.program_name && `**Program:** ${es.program_name}`,
      es.model && `**Model:** ${es.model}`,
      es.initial_investment && `**Initial Investment:** ${es.initial_investment.amount} ${es.initial_investment.currency || 'USD'} — ${es.initial_investment.coverage || ''}`,
      es.projected_user_base != null && `**Projected User Base:** ${es.projected_user_base}`,
      es.break_even_months != null && `**Break-even:** Month ${es.break_even_months}`,
      es.first_year_roi_percent != null && `**Year 1 ROI:** ${es.first_year_roi_percent}%`,
    ].filter(Boolean).join('\n\n');
    sections.push({
      id: 'executive_summary',
      order: order++,
      type: 'section',
      title: 'Executive Summary',
      title_pt: 'Sumário Executivo',
      level: 1,
      include_page_break: true,
      content: content || objToContent(es),
      subsections: [],
      tables: [],
    });
  }

  if (bm) {
    const subsections = [];
    const ps = bm.program_structure || bm.programStructure;
    if (ps) {
      subsections.push({
        id: 'program_structure',
        type: 'subsection',
        title: 'Program Structure',
        title_pt: 'Estrutura do Programa',
        level: 2,
        content: objToContent(ps),
        tables: [],
      });
    }
    const rs = bm.revenue_streams || bm.revenueStreams;
    if (rs) {
      const primary = (rs.primary || []).map((p) => [
        p.source || '',
        p.percentage ?? (p.fee_per_card != null ? `${p.fee_per_card} ${p.currency || 'EUR'}` : p.fee_per_month != null ? `${p.fee_per_month} ${p.currency || 'EUR'}/mo` : ''),
        p.description || '',
      ]);
      subsections.push({
        id: 'revenue_streams',
        type: 'subsection',
        title: 'Revenue Streams',
        title_pt: 'Fontes de Receita',
        level: 2,
        content: '',
        tables: primary.length ? [{ headers: ['Source', 'Fee/Share', 'Description'], headers_pt: ['Fonte', 'Taxa/Participação', 'Descrição'], rows: primary }] : [],
      });
    }
    sections.push({
      id: 'business_model',
      order: order++,
      type: 'section',
      title: 'Business Model',
      title_pt: 'Modelo de Negócio',
      level: 1,
      include_page_break: true,
      content: objToContent(bm),
      subsections,
      tables: [],
    });
  }

  const forecast = fp?.['12_month_forecast'] || fp?.['12MonthForecast'];
  if (forecast) {
    const headers = ['Month', 'Clients', 'Volume (EUR)', 'Costs', 'Revenues', 'Profit', 'Net Position'];
    const headers_pt = ['Mês', 'Clientes', 'Volume (EUR)', 'Custos', 'Receitas', 'Lucro', 'Posição Líquida'];
    const rows = forecast.map((m) => [
      String(m.month),
      String(m.clients ?? '-'),
      String(m.metrics?.total_monthly_volume_eur ?? '-'),
      String(m.costs?.total_costs ?? '-'),
      String(m.revenues?.total_revenues ?? '-'),
      String(m.profit ?? '-'),
      String(m.net_position ?? '-'),
    ]);
    sections.push({
      id: 'financial_projections',
      order: order++,
      type: 'section',
      title: '12-Month Financial Projections',
      title_pt: 'Projeções Financeiras 12 Meses',
      level: 1,
      include_page_break: true,
      content: '',
      tables: [{ headers, headers_pt, rows }],
      subsections: [],
    });
  }

  if (as) {
    const content = [
      as.total_volume_eur && `**Total Volume Year 1:** €${(as.total_volume_eur.year1 || as.total_volume_eur).toLocaleString()}`,
      as.total_revenues_eur && `**Total Revenues:** €${(as.total_revenues_eur?.total || as.total_revenues_eur).toLocaleString()}`,
      as.gross_profit_eur != null && `**Gross Profit:** €${as.gross_profit_eur.toLocaleString()}`,
      as.net_profit_after_initial_investment_eur != null && `**Net Profit (after initial investment):** €${as.net_profit_after_initial_investment_eur.toLocaleString()}`,
      as.roi_percent != null && `**ROI:** ${as.roi_percent}%`,
    ].filter(Boolean).join('\n\n');
    sections.push({
      id: 'annual_summary',
      order: order++,
      type: 'section',
      title: 'Annual Summary',
      title_pt: 'Resumo Anual',
      level: 1,
      include_page_break: true,
      content: content || objToContent(as),
      subsections: [],
      tables: [],
    });
  }

  if (ftc) {
    const flowRows = (ftc.flow || []).map((f) => [String(f.step), f.action || '', f.fiddux_responsibility || '', f.cost || '']);
    sections.push({
      id: 'fiat_to_crypto',
      order: order++,
      type: 'section',
      title: 'Fiat to Crypto Flow',
      title_pt: 'Fluxo Fiat para Crypto',
      level: 1,
      include_page_break: true,
      content: ftc.description || '',
      tables: flowRows.length ? [{ headers: ['Step', 'Action', 'FIDDUX Responsibility', 'Cost'], headers_pt: ['Passo', 'Ação', 'Responsabilidade FIDDUX', 'Custo'], rows: flowRows }] : [],
      subsections: [],
    });
  }

  if (rm && Array.isArray(rm)) {
    const rows = rm.map((r) => [r.risk || '', r.likelihood || '', r.mitigation || '']);
    sections.push({
      id: 'risk_mitigation',
      order: order++,
      type: 'section',
      title: 'Risk Mitigation',
      title_pt: 'Mitigação de Riscos',
      level: 1,
      include_page_break: true,
      content: '',
      tables: [{ headers: ['Risk', 'Likelihood', 'Mitigation'], headers_pt: ['Risco', 'Probabilidade', 'Mitigação'], rows }],
      subsections: [],
    });
  }

  if (rec) {
    const content = [
      rec.approval && `**Approval:** ${rec.approval}`,
      rec.rationale?.length && `**Rationale:**\n${rec.rationale.map((r) => `- ${r}`).join('\n')}`,
      rec.next_steps?.length && `**Next Steps:**\n${rec.next_steps.map((s) => `- ${s}`).join('\n')}`,
      rec.timeline && `**Timeline:** ${rec.timeline}`,
      rec.owner && `**Owner:** ${rec.owner}`,
    ].filter(Boolean).join('\n\n');
    sections.push({
      id: 'recommendations',
      order: order++,
      type: 'section',
      title: 'Recommendations',
      title_pt: 'Recomendações',
      level: 1,
      include_page_break: true,
      content: content || objToContent(rec),
      subsections: [],
      tables: [],
    });
  }

  if (app) {
    const content = [
      app.key_assumptions?.length && `**Key Assumptions:**\n${app.key_assumptions.map((a) => `- ${a}`).join('\n')}`,
      app.data_sources?.length && `**Data Sources:**\n${app.data_sources.map((s) => `- ${s}`).join('\n')}`,
      app.prepared_by && `**Prepared by:** ${app.prepared_by}`,
      app.confidentiality && `**Confidentiality:** ${app.confidentiality}`,
    ].filter(Boolean).join('\n\n');
    sections.push({
      id: 'appendix',
      order: order++,
      type: 'section',
      title: 'Appendix',
      title_pt: 'Apêndice',
      level: 1,
      include_page_break: true,
      content: content || objToContent(app),
      subsections: [],
      tables: [],
    });
  }

  return { report_meta, sections };
}

/**
 * Validate and parse JSON input
 */
export function parseJsonToSchema(content) {
  try {
    const normalized = normalizeJsonString(content);
    const parsed = JSON.parse(normalized);
    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Invalid JSON structure');
    }

    const defaultBranding = {
      company_name: 'FIDDUX',
      accent_color: '#00d4ff',
      primary_color: '#1a1a2e',
      font_primary: 'Inter, sans-serif',
    };

    let report_meta;
    let sections;

    if (isAlternativeJsonFormat(parsed)) {
      const converted = convertAlternativeFormatToSchema(parsed);
      report_meta = converted.report_meta;
      sections = converted.sections;
    } else {
      report_meta = {
        ...DEFAULT_REPORT_META,
        ...(parsed.report_meta || {}),
      };
      sections = Array.isArray(parsed.sections) ? parsed.sections : [];
    }

    const schema = {
      report_meta,
      sections,
      footer: {
        disclaimer: parsed.footer?.disclaimer || parsed.footer?.disclaimer_en || 'This document is strictly confidential and intended solely for authorized recipients. It contains privileged information and may not be reproduced, distributed, or disclosed without prior written consent from FIDDUX.',
        disclaimer_pt: parsed.footer?.disclaimer_pt || 'Este documento é estritamente confidencial e destinado exclusivamente a destinatários autorizados. Contém informações privilegiadas e não pode ser reproduzido, distribuído ou divulgado sem consentimento prévio por escrito da FIDDUX.',
        branding_elements: {
          ...defaultBranding,
          ...(parsed.footer?.branding_elements || {}),
        },
      },
    };

    return schema;
  } catch (e) {
    const msg = e.message || '';
    const posMatch = msg.match(/position (\d+)/);
    const hint = posMatch ? ` (verifique caracteres especiais perto da posição ${posMatch[1]})` : '';
    throw new Error(`JSON inválido: ${msg}${hint}`);
  }
}

/**
 * Parse content (auto-detect format) into unified schema
 */
export function parseContent(content, inputType = null) {
  const detected = inputType || detectInputType(content);
  if (detected === 'json') {
    return parseJsonToSchema(content);
  }
  return parseMarkdownToSchema(content);
}

function applyLangToTable(t, lang) {
  return {
    ...t,
    _headers: lang === 'pt' ? (t.headers_pt || t.headers) : (t.headers || t.headers_pt),
    _rows: lang === 'pt' ? (t.rows_pt || t.rows) : (t.rows || t.rows_pt),
    _highlightRow: lang === 'pt' ? (t.highlight_row_pt || t.highlight_row) : (t.highlight_row || t.highlight_row_pt),
  };
}

function applyLangToSection(s, lang) {
  const _title = lang === 'pt' ? (s.title_pt || s.title) : (s.title || s.title_pt);
  const _content = lang === 'pt' ? (s.content_pt || s.content) : (s.content || s.content_pt);
  const res = {
    ...s,
    _title,
    _content,
    _tables: (s.tables || []).map((t) => applyLangToTable(t, lang)),
  };
  if (s.subsections && s.subsections.length > 0) {
    res._subsections = s.subsections.map((ss) => applyLangToSubsection(ss, lang));
  }
  const boxes = s.boxes || s.highlight_boxes || [];
  if (boxes.length > 0) {
    res._boxes = boxes.map((b) => ({
      ...b,
      _title: lang === 'pt' ? (b.title_pt || b.title) : (b.title || b.title_pt),
      _content: lang === 'pt' ? (b.content_pt || b.content) : (b.content || b.content_pt),
    }));
  }
  if (s.chart) {
    res._chart = {
      ...s.chart,
      _title: lang === 'pt' ? (s.chart.title_pt || s.chart.title) : (s.chart.title || s.chart.title_pt),
    };
  }
  return res;
}

function applyLangToSubsection(ss, lang) {
  const _title = lang === 'pt' ? (ss.title_pt || ss.title) : (ss.title || ss.title_pt);
  const _content = lang === 'pt' ? (ss.content_pt || ss.content) : (ss.content || ss.content_pt);
  const res = {
    ...ss,
    _title,
    _content,
    _tables: (ss.tables || []).map((t) => applyLangToTable(t, lang)),
  };
  if (ss.chart) {
    res._chart = {
      ...ss.chart,
      _title: lang === 'pt' ? (ss.chart.title_pt || ss.chart.title) : (ss.chart.title || ss.chart.title_pt),
    };
  }
  if (ss.boxes && ss.boxes.length > 0) {
    res._boxes = ss.boxes.map((b) => ({
      ...b,
      _title: lang === 'pt' ? (b.title_pt || b.title) : (b.title || b.title_pt),
      _content: lang === 'pt' ? (b.content_pt || b.content) : (b.content || b.content_pt),
    }));
  }
  return res;
}

/**
 * Apply language selection to schema (swap content fields)
 */
export function applyLanguage(schema, language) {
  if (language === 'pt' || language === 'en') {
    const footer = schema.footer || {};
    return {
      ...schema,
      _activeLang: language,
      sections: (schema.sections || []).map((s) => applyLangToSection(s, language)),
      _disclaimer: language === 'pt' ? (footer.disclaimer_pt || footer.disclaimer) : (footer.disclaimer || footer.disclaimer_en || footer.disclaimer_pt),
    };
  }
  return schema;
}

/**
 * Convert markdown string to HTML (for rendering)
 */
export function markdownToHtml(md) {
  if (!md) return '';
  return marked.parse(md, { gfm: true });
}
